import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { templateService, type Template } from '../../services/template.service';
import { accountService } from '../../services/account.service';
import { useToast } from '../../components/common/Toast';
import { SEOHead } from '../../components/seo/SEOHead';
import { generateOrganizationSchema, generateWebSiteSchema } from '../../utils/structured-data';
import { LandingNav } from './LandingNav';
import { BootOverlay } from './BootOverlay';
import { HeroSection } from './HeroSection';
import { useComposer } from './useComposer';
import { HowItWorks } from './HowItWorks';
import { ServicesSection } from './ServicesSection';
import { TemplatesSection } from './TemplatesSection';
import { toTplCard } from './TemplateCard';
import { QuoteSection } from './QuoteSection';
import { CtaSection } from './CtaSection';
import { TrustedBySection } from './TrustedBySection';
import { LandingFooter } from './LandingFooter';
import { TopUpDialog, type SettleStatus } from './TopUpDialog';
import { useLandingMotion } from './useLandingMotion';
import { useLandingTheme } from './useLandingTheme';
import { FONT_SANS, VIDEO_ANNOUNCEMENT } from './landingData';
import { useApplyTemplate } from './useApplyTemplate';
import './landing.css';

export const LandingPage: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user, isAuthenticated, isLoading: authLoading, logout, refreshUser } = useAuth();
    const rootRef = useRef<HTMLDivElement>(null);

    const [theme, toggleTheme] = useLandingTheme();

    const [templates, setTemplates] = useState<Template[]>([]);
    const [templatesLoading, setTemplatesLoading] = useState(true);

    const [credits, setCredits] = useState<number | null>(null);
    const [topUpOpen, setTopUpOpen] = useState(false);
    const toast = useToast();
    const showStatus = (status: SettleStatus, message: string) =>
        toast.show(message, { tone: status === 'SUCCESS' ? 'success' : status === 'PENDING' ? 'warn' : 'error', label: status, duration: 6000 });

    const composer = useComposer({ isAuthenticated, notify: showStatus });
    const { product, pickProduct, setPrompt, boot, bootKind, launch, abortLaunch } = composer;

    useLandingMotion(rootRef, [isAuthenticated, templatesLoading, product]);

    // ---- credits: only once auth has resolved AND the visitor is logged in.
    // An anonymous request would 401 and api.ts would bounce them to /login. ----
    useEffect(() => {
        if (authLoading) return;
        if (isAuthenticated) void accountService.getCredits().then((r) => { if (r.success && r.data) setCredits(r.data.credits); });
        else setCredits(null);
    }, [authLoading, isAuthenticated]);

    useEffect(() => {
        if (isAuthenticated && credits === null && typeof user?.credits === 'number') setCredits(user.credits);
    }, [isAuthenticated, user, credits]);

    // ---- templates (public catalog) ----
    useEffect(() => {
        void (async () => {
            const res = await templateService.list();
            if (res.success && res.data) setTemplates(res.data);
            setTemplatesLoading(false);
        })();
    }, []);

    // ---- composer ----
    const focusComposer = useCallback(() => {
        const el = document.querySelector<HTMLElement>('.hv-composer');
        if (!el) return;
        const target = el.getBoundingClientRect().top - 120;
        window.scrollTo({ top: window.scrollY + target, behavior: 'smooth' });
        const ta = el.querySelector('textarea');
        if (ta) setTimeout(() => ta.focus({ preventScroll: true }), 620);
    }, []);

    // Signup hands over the result of its "Referral code" box: show it once.
    useEffect(() => {
        const notice = (location.state as { codeNotice?: { tone: 'success' | 'error' | 'warn'; text: string } } | null)?.codeNotice;
        if (!notice) return;
        toast.show(notice.text, { tone: notice.tone, label: notice.tone === 'success' ? 'KODE' : notice.tone === 'warn' ? 'INFO' : 'KODE', duration: 8000 });
        navigate('/', { replace: true, state: null });
    }, [location.state]); // eslint-disable-line react-hooks/exhaustive-deps

    // A use-case page's "Build one like this" lands here with a seed prompt in
    // router state: fill the composer, clear the state so a reload doesn't repeat it.
    useEffect(() => {
        const seed = (location.state as { prompt?: string } | null)?.prompt;
        if (!seed) return;
        setPrompt(seed);
        navigate('/', { replace: true, state: null });
        const id = setTimeout(focusComposer, 80);
        return () => clearTimeout(id);
    }, [location.state, navigate, focusComposer]);

    // Arriving at "/#templates" from /pricing or a use-case page: the section only
    // exists after this page renders, so the browser's native hash scroll missed it.
    useEffect(() => {
        if (!location.hash) return;
        const el = document.getElementById(location.hash.slice(1));
        if (!el) return;
        const id = setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 60);
        return () => clearTimeout(id);
    }, [location.hash]);


    const { applying: applyingTemplate, apply: applyTemplate } = useApplyTemplate({
        isAuthenticated,
        launch,
        abortLaunch,
    });
    const handleUseTemplate = (templateId: string) => {
        const template = templates.find((t) => t.id === templateId);
        if (template) void applyTemplate(template);
    };

    const onTopUpSettled = async (status: SettleStatus, message: string) => {
        showStatus(status, message);
        if (status === 'SUCCESS') {
            await refreshUser();
            const r = await accountService.getCredits();
            if (r.success && r.data) setCredits(r.data.credits);
        }
    };

    const tplCards = useMemo(() => templates.map(toTplCard), [templates]);

    return (
        <div ref={rootRef} className="hv-root" data-hv-theme={theme} style={{ position: 'static', fontFamily: FONT_SANS, color: 'var(--hv-ink)', background: 'var(--hv-desk)', overflowX: 'clip', minHeight: '100vh', WebkitFontSmoothing: 'antialiased' }}>
            <SEOHead
                title="HyVay - Platform No-Code App Builder Indonesia"
                description="Buat aplikasi web profesional tanpa coding dengan HyVay. Platform No-Code App Builder terbaik di Indonesia. Cepat, Mudah, dan Terjangkau."
                keywords={['no-code', 'app builder', 'indonesia', 'bikin aplikasi', 'website builder', 'startup']}
                structuredData={[generateOrganizationSchema(), generateWebSiteSchema()]}
            />

            <HeroSection
                nav={(
                    <LandingNav
                        theme={theme}
                        onToggleTheme={toggleTheme}
                        isAuthenticated={isAuthenticated}
                        avatarUrl={user?.avatar}
                        userName={user?.fullName}
                        onLogout={logout}
                    />
                )}
                {...composer.cardProps(isAuthenticated ? (credits ?? 0) : null, () => setTopUpOpen(true))}
                chips={composer.chips}
                onShuffle={composer.shuffle}
                onFillChip={composer.fillChip}
                announcement={VIDEO_ANNOUNCEMENT}
                onAnnouncement={() => { pickProduct('video'); focusComposer(); }}
            />

            <ServicesSection />

            <HowItWorks product={product} onProduct={pickProduct} />

            <TemplatesSection
                templates={tplCards}
                loading={templatesLoading}
                busyId={applyingTemplate}
                onUse={handleUseTemplate}
                onTryVideo={() => { pickProduct('video'); focusComposer(); }}
            />

            <QuoteSection />

            <TrustedBySection />

            <CtaSection onStart={focusComposer} />

            <LandingFooter />

            <TopUpDialog open={topUpOpen} onClose={() => setTopUpOpen(false)} onSettled={onTopUpSettled} />

            {boot && <BootOverlay copy={boot} kind={bootKind} dark={theme === 'dark'} />}

        </div>
    );
};

export default LandingPage;
